/**
 * PURPOSE: Asignar el uniforme de cada lado del partido desde las opciones registradas del equipo (FTR-0014).
 * RESPONSIBILITIES: Cargar el contexto del partido (clases que la política permite, opciones del equipo exacto de
 *   cada lado, texto vigente y enlace), seleccionar por ID y exponer solo los cambios. Un lado con texto legacy
 *   sin enlace queda intacto salvo que se elija una opción. No edita el catálogo.
 * COLLABORATORS: uniformOptionsService.js, uniformOptions.utils.js, UniformOptionVisual.jsx,
 *   UniformPhotoLightbox.jsx (foto ampliada, FTR-0039), ModalProgramacion.jsx.
 * ROUTE: winscore/src/components/Modals/ModalProgramacion/MatchUniformOptionsFields.jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";

import UniformOptionVisual from "../../../admin/AdminCompany/UniformOptionVisual.jsx";
import UniformPhotoLightbox from "../../../admin/AdminCompany/UniformPhotoLightbox.jsx";
import {
  changedAssignments,
  contextualOptionLabel,
  getUniformErrorMessage,
  initialSelections,
  kindLabel,
  optionPositionLabel,
} from "../../../admin/AdminCompany/uniformOptions.utils.js";
import { getMatchUniformContext } from "../../../services/uniformOptionsService.js";

/** Estado del contexto del partido y de la selección; solo consulta cuando el modelo normalizado está activo. */
export const useMatchUniformAssignment = (idmatch, enabled) => {
  const [context, setContext] = useState(null);
  const [initial, setInitial] = useState({});
  const [selections, setSelections] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    if (!enabled || !idmatch) return;
    setLoading(true);
    setError("");
    try {
      const next = await getMatchUniformContext(idmatch);
      const start = initialSelections(next);
      setContext(next);
      setInitial(start);
      setSelections(start);
    } catch (loadError) {
      setContext(null);
      setError(getUniformErrorMessage(loadError, "No se pudieron cargar los uniformes del partido."));
    } finally {
      setLoading(false);
    }
  }, [enabled, idmatch]);

  useEffect(() => {
    reload();
  }, [reload]);

  const changes = useMemo(() => changedAssignments(initial, selections), [initial, selections]);
  const select = (key, idoption) => setSelections((current) => ({ ...current, [key]: idoption }));
  return { context, selections, select, changes, loading, error, reload };
};

const NONE = { value: null, label: "Sin asignar" };

// Un mismo selector reúne alternativas de distinto tipo y tono, así que el texto principal es la etiqueta
// contextual ("Camiseta clara — Celeste con Blanco"); el orden aparece solo como metadato secundario, para
// no confundir dos opciones con el mismo color predominante.
// FTR-0039: la foto, tanto en el menú como en el valor elegido, se amplía con un clic sobre ella; el clic no
// selecciona la opción ni abre el menú, y el visor vive fuera del select (su menú se desmonta al perder el foco).
const buildFormatOptionLabel = (onPhotoClick) => (item) => (
  <span className="flex items-center gap-2">
    {/* Foto Y swatches, con retirada solo de la foto si la imagen falla (corrección 2026-09-14). */}
    {item.option && (
      <UniformOptionVisual
        option={item.option}
        size="xs"
        swatchSize="sm"
        alt={item.label}
        showPlaceholder={false}
        onPhotoClick={onPhotoClick}
        photoTabIndex={-1}
        // En el valor elegido, el input (transparente) de react-select se superpone a la etiqueta en la misma
        // celda de la grilla: sin elevarla, el clic real cae en el input y no en la foto.
        photoClassName="relative z-[1]"
      />
    )}
    <span>{item.label}</span>
    {item.option?.order && <span className="text-xs text-gray-400">{optionPositionLabel(item.option.order)}</span>}
  </span>
);

const MatchUniformOptionsFields = ({ state, lockedReason = "" }) => {
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const { context, selections, select, loading, error } = state;
  if (loading) return <p className="text-sm text-gray-500 sm:col-span-2">Cargando uniformes del partido…</p>;
  if (error) return <p className="text-sm text-rose-600 sm:col-span-2">{error}</p>;
  if (!context) return null;
  const formatOptionLabel = buildFormatOptionLabel(setEnlargedPhoto);

  return (
    <>
      {lockedReason && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 sm:col-span-2">{lockedReason}</p>}
      {context.kinds.map(({ kind, idtype }) =>
        context.sides.map((side) => {
          const key = `${side.side}:${kind}`;
          const current = side.current?.[kind] || {};
          const items = [NONE, ...(side.options?.[kind] || []).map((option) => ({ value: option.id, label: contextualOptionLabel(option), option }))];
          const value = items.find((item) => item.value === (selections[key] ?? null)) || NONE;
          const hasLegacyText = Boolean(current.text) && (!current.option || current.stale);
          return (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700">
                {kindLabel({ kind, idtype })} del Equipo {side.side}
              </label>
              <div className="mt-1">
                <Select
                  inputId={`uniform-${key}`}
                  value={value}
                  options={items}
                  isDisabled={Boolean(lockedReason) || !side.team}
                  formatOptionLabel={formatOptionLabel}
                  onChange={(item) => select(key, item?.value ?? null)}
                  placeholder={side.team ? "Elegir opción" : "Sin equipo"}
                  noOptionsMessage={() => "El equipo no tiene opciones registradas"}
                  classNamePrefix="react-select"
                  styles={{ control: (base) => ({ ...base, minHeight: 38 }), menu: (base) => ({ ...base, zIndex: 30 }) }}
                />
              </div>
              {!side.team && <p className="mt-1 text-xs text-gray-500">Este lado no tiene un equipo con uniformes registrados.</p>}
              {side.team && items.length === 1 && <p className="mt-1 text-xs text-gray-500">El equipo no tiene opciones registradas para este uniforme.</p>}
              {hasLegacyText && (
                <p className="mt-1 text-xs text-amber-700">
                  Texto actual: “{current.text}”{current.stale ? " (enlace obsoleto)" : ""}. Se conserva salvo que elijas una opción.
                </p>
              )}
            </div>
          );
        })
      )}
      <UniformPhotoLightbox
        open={Boolean(enlargedPhoto)}
        photoUrl={enlargedPhoto?.photoUrl}
        label={enlargedPhoto?.label}
        onClose={() => setEnlargedPhoto(null)}
      />
    </>
  );
};

export default MatchUniformOptionsFields;
